<template>
	<div>
		<div
			class="moments-lane l-content-section"
		>
			<div class="moments-lane__row">
				<div class="moments-lane__title-vertical">
					{{ $I18n.trans_choice('commons.moment', 2).toLowerCase() }}
				</div>
				<div
					class="moments-lane__carousel"
				>
					<Carousel
						ref="momentsLaneCarousel"
						:cells="carouselItems"
						:group-cells="'100%'"
					>
						<template #cell="{ cell, isDragging }">
							<a
								:key="cell.id"
								class="moments-lane__cell moments-lane__group"
								:class="{
									'-highlighted': isHighlighted(cell),
									'-loading': isGroupLoading(cell),
								}"
								href="#"
								@click.prevent="openGroup(cell, isDragging)"
							>
								<div class="moments-lane__group-inner">
									<div
										v-if="isGroupLoading(cell)"
										class="moments-lane__loading-overlay"
										aria-hidden="true"
									>
										<div class="moments-lane__loading-spinner" />
									</div>
									<img
										:src="cell.participants?.[0]?.imageUrl"
										:alt="cell.participants?.[0]?.clubCode || ''"
										class="moments-lane__club-logo -top"
									>
									<img
										:src="cell.participants?.[1]?.imageUrl"
										:alt="cell.participants?.[1]?.clubCode || ''"
										class="moments-lane__club-logo -bottom"
									>
									<div class="moments-lane__tags">
										<div class="moments-lane__tag -top">
											{{ cell.participants?.[0]?.clubCode || '' }}
										</div>
										<div class="moments-lane__tag -bottom">
											{{ cell.participants?.[1]?.clubCode || '' }}
										</div>
									</div>
								</div>
							</a>
						</template>
					</Carousel>
				</div>
			</div>
		</div>
		<div
			v-if="isPlayerReady"
			class="moments-lane__hidden-player"
		>
			<MomentsStoriesPlayer
				ref="storiesPlayer"
				:items="sortedItems"
				@group-complete="handleGroupComplete"
				@availability-changed="handlePlayerAvailability"
				@story-opened="handleStoryOpened"
				@story-open-failed="handleStoryOpenFailed"
			/>
		</div>
	</div>
</template>

<script>
import Carousel from '@js/components/Carousel';
import MomentsStoriesPlayer from '@js/components/MomentsStoriesPlayer';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'MomentsLane',
	components: { Carousel, MomentsStoriesPlayer },
	props:      {
		items: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			watchedGroupsById:   {},
			watchStateLoaded:    false,
			loadingGroupsById:   {},
			resolvedItems:       [],
			isResolvingPlayouts: false,
			playoutResolveRunId: 0,
			hasResolvedPlayouts: false,
		};
	},
	computed: {
		sortedItems() {
			return [...this.resolvedItems].sort((a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime());
		},
		carouselItems() {
			if (this.sortedItems.length) {
				return this.sortedItems;
			}

			return this.toItemsArray(this.items);
		},
		isPlayerReady() {
			return this.hasResolvedPlayouts && this.sortedItems.length > 0;
		},
		storageKey() {
			return `arena:moments:watch:v1:${this.$arenaConfig.domainKey}:${this.$arenaConfig.id}`;
		},
	},
	watch: {
		items: {
			handler() {
				this.loadingGroupsById = {};
				this.loadWatchedGroups();
				this.resetPlayoutResolutionState();
			},
			deep: true,
		},
		hasResolvedPlayouts(isResolved) {
			if (!isResolved) {
				return;
			}

			this.$nextTick(() => {
				this.$refs.momentsLaneCarousel?.rerender();
			});
		},
	},
	mounted() {
		this.loadWatchedGroups();
		this.resetPlayoutResolutionState();
	},
	methods: {
		toItemsArray(items) {
			if (Array.isArray(items)) {
				return items;
			}

			if (items && typeof items === 'object') {
				return Object.values(items);
			}

			return [];
		},
		normalizeMoment(moment = {}) {
			const cropId = moment?.eventVideoCropId || moment?.id;
			const normalizedCropId = cropId ? String(cropId) : null;
			const normalizedId = moment?.id ? String(moment.id) : normalizedCropId;
			const normalizedVideoUrl = typeof moment?.videoUrl === 'string' && moment.videoUrl.length > 0
				? moment.videoUrl
				: null;

			return {
				...moment,
				id:               normalizedId,
				eventVideoCropId: normalizedCropId,
				videoUrl:         normalizedVideoUrl,
			};
		},
		normalizeGroup(group = {}) {
			return {
				...group,
				id:      String(group?.id || ''),
				moments: (group.moments || []).map(this.normalizeMoment),
			};
		},
		getMomentCropId(moment) {
			return String(moment?.eventVideoCropId || '');
		},
		normalizeItemsWithResolvedPlayouts(items = [], videoUrlByCropId = {}) {
			return this.toItemsArray(items).map(group => this.normalizeGroup({
				...group,
				moments: (group.moments || []).map((rawMoment) => {
					const moment = this.normalizeMoment(rawMoment);
					const cropId = this.getMomentCropId(moment);
					return {
						...moment,
						videoUrl: moment.videoUrl || (cropId ? (videoUrlByCropId[cropId] || null) : null),
					};
				}),
			}));
		},
		getMissingMomentCropIds(items = []) {
			const cropIds = [];
			this.toItemsArray(items).forEach((group) => {
				(group.moments || []).forEach((moment) => {
					if (moment.videoUrl) {
						return;
					}

					const cropId = this.getMomentCropId(moment);
					if (!cropId) {
						return;
					}

					cropIds.push(cropId);
				});
			});

			return [...new Set(cropIds)];
		},
		resetPlayoutResolutionState() {
			this.playoutResolveRunId += 1;
			this.isResolvingPlayouts = false;
			this.hasResolvedPlayouts = false;
			this.resolvedItems = this.normalizeItemsWithResolvedPlayouts(this.items);
		},
		async ensurePlayoutsResolved() {
			if (this.hasResolvedPlayouts) {
				return true;
			}

			if (this.isResolvingPlayouts) {
				return false;
			}

			const runId = this.playoutResolveRunId + 1;
			this.playoutResolveRunId = runId;
			const sourceItems = this.toItemsArray(this.items);
			const missingCropIds = this.getMissingMomentCropIds(sourceItems);
			if (!missingCropIds.length) {
				this.resolvedItems = this.normalizeItemsWithResolvedPlayouts(sourceItems);
				this.hasResolvedPlayouts = true;
				return true;
			}

			this.isResolvingPlayouts = true;
			try {
				const response = await httpClient.getMomentsLanePlayouts(missingCropIds);
				if (runId !== this.playoutResolveRunId) {
					return false;
				}

				const responseMap = response?.data?.videoUrlByCropId;
				const videoUrlByCropId = responseMap && typeof responseMap === 'object' ? responseMap : {};
				this.resolvedItems = this.normalizeItemsWithResolvedPlayouts(sourceItems, videoUrlByCropId);
				this.hasResolvedPlayouts = true;
				return true;
			} catch {
				if (runId !== this.playoutResolveRunId) {
					return false;
				}

				this.resolvedItems = this.normalizeItemsWithResolvedPlayouts(sourceItems);
				this.hasResolvedPlayouts = false;
				return false;
			} finally {
				if (runId === this.playoutResolveRunId) {
					this.isResolvingPlayouts = false;
				}
			}
		},
		loadWatchedGroups() {
			try {
				const parsed = JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
				this.watchedGroupsById = typeof parsed === 'object' && parsed ? parsed : {};
			} catch {
				this.watchedGroupsById = {};
			} finally {
				this.watchStateLoaded = true;
			}
		},
		saveWatchedGroups() {
			window.localStorage.setItem(this.storageKey, JSON.stringify(this.watchedGroupsById));
		},
		getMomentIds(group) {
			return (group.moments || []).map(moment => moment.id);
		},
		hasNewVideo(group, watchedGroup) {
			const watchedMomentIds = Array.isArray(watchedGroup?.seenMomentIds) ? watchedGroup.seenMomentIds : [];
			const groupMomentIds = this.getMomentIds(group);
			const hasNewMomentId = groupMomentIds.some(momentId => !watchedMomentIds.includes(momentId));
			const hasNewTimestamp = !watchedGroup?.lastSeenUpdatedAt ||
				new Date(group.lastUpdatedAt).getTime() > new Date(watchedGroup.lastSeenUpdatedAt).getTime();

			return hasNewMomentId || hasNewTimestamp;
		},
		isHighlighted(group) {
			if (!this.watchStateLoaded) {
				return false;
			}

			const watchedGroup = this.watchedGroupsById[group.id];
			if (!watchedGroup) {
				return true;
			}

			return this.hasNewVideo(group, watchedGroup);
		},
		isGroupLoading(group) {
			return Boolean(this.loadingGroupsById[String(group.id)]);
		},
		setGroupLoading(groupId, isLoading) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return;
			}

			if (isLoading) {
				this.loadingGroupsById = {
					...this.loadingGroupsById,
					[normalizedGroupId]: true,
				};
				return;
			}

			const { [normalizedGroupId]: _removed, ...restLoadingGroupsById } = this.loadingGroupsById;
			this.loadingGroupsById = restLoadingGroupsById;
		},
		async openGroup(group, isDragging) {
			if (isDragging || this.isResolvingPlayouts) {
				return;
			}

			const groupId = String(group?.id || '');
			if (!groupId) {
				return;
			}

			if (!this.hasResolvedPlayouts) {
				this.setGroupLoading(groupId, true);
				const didResolve = await this.ensurePlayoutsResolved();
				if (!didResolve) {
					this.setGroupLoading(groupId, false);
					return;
				}

				await this.$nextTick();
			}

			const openResult = this.$refs.storiesPlayer?.openStory(groupId);
			this.setGroupLoading(groupId, Boolean(openResult?.waiting));
		},
		handlePlayerAvailability() {
		},
		handleStoryOpened(groupId) {
			this.setGroupLoading(groupId, false);
		},
		handleStoryOpenFailed(groupId) {
			this.setGroupLoading(groupId, false);
		},
		handleGroupComplete(groupId) {
			const group = this.sortedItems.find(item => String(item.id) === String(groupId));
			if (!group) {
				return;
			}

			this.watchedGroupsById = {
				...this.watchedGroupsById,
				[group.id]: {
					lastSeenUpdatedAt: group.lastUpdatedAt,
					seenMomentIds:     this.getMomentIds(group),
					watchedAt:         new Date().toISOString(),
				},
			};

			this.saveWatchedGroups();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.moments-lane {
	--moments-lane-angle: -8.6deg;
	--color-moments-dark: var(--color-black);
	--color-moments-light: var(--color-grey-600);
	--moments-lane-row-gap: #{$sp3};
	--moments-lane-title-size: 22px;
	--moments-lane-title-width: 38px;
	--moments-lane-card-overflow: calc(#{$carousel-card-overflow} * 0.75);
	--moments-lane-cell-width: 77px;
	--moments-lane-cell-height: 114px;
	--moments-lane-card-width: 74px;
	--moments-lane-card-height: 114px;
	--moments-lane-card-top-slice-height: 62px;
	--moments-lane-card-bottom-slice-height: 64px;
	--moments-lane-logo-size: 23px;
	--moments-lane-logo-offset: 5px;
	--moments-lane-tag-size: 24px;
	--moments-lane-bg-top: -21px;
	--moments-lane-bg-left: 33px;
	--moments-lane-bg-height-extra: 42px;
	position: relative;

	&::after {
		content: '';
		display: block;
		position: absolute;
		top: var(--moments-lane-bg-top);
		left: var(--moments-lane-bg-left);
		width: calc(100% - var(--moments-lane-bg-left));
		height: calc(100% + var(--moments-lane-bg-height-extra));
		background-color: var(--color-grey-950);
		z-index: -1;
		border-radius: var(--t-border-radius-card--lg);
	}

	@include size(md-up) {
		--moments-lane-row-gap: #{$sp4};
		--moments-lane-title-size: 32px;
		--moments-lane-title-width: 50px;
		--moments-lane-card-overflow: #{$carousel-card-overflow};
		--moments-lane-cell-width: 102px;
		--moments-lane-cell-height: 152px;
		--moments-lane-card-width: 99px;
		--moments-lane-card-height: 152px;
		--moments-lane-card-top-slice-height: 83px;
		--moments-lane-card-bottom-slice-height: 85px;
		--moments-lane-logo-size: 30px;
		--moments-lane-logo-offset: 7px;
		--moments-lane-tag-size: 32px;
		--moments-lane-bg-top: -28px;
		--moments-lane-bg-left: 44px;
		--moments-lane-bg-height-extra: 56px;
	}

	body.-t-light & { // stylelint-disable-line
		--color-moments-dark: var(--color-grey-200);
		--color-moments-light: var(--color-grey-800);
	}
}

.moments-lane__hidden-player {
	position: relative;
	width: 0;
	height: 0;
	overflow: visible;
}

.moments-lane__row {
	display: flex;
	align-items: center;
	gap: var(--moments-lane-row-gap);
}

.moments-lane__title-vertical {
	writing-mode: vertical-rl;
	transform: rotate(180deg);
	font-weight: 300; // stylelint-disable-line
	font-size: var(--moments-lane-title-size); // stylelint-disable-line
	text-transform: lowercase;
	letter-spacing: -1px; // stylelint-disable-line
	flex-shrink: 0;
	line-height: 1;
	width: var(--moments-lane-title-width);
}

.moments-lane__carousel {
	width: 100%;
	min-width: 0;
}

.moments-lane__cell {
	display: block;
	width: calc(var(--moments-lane-cell-width) + (2 * var(--moments-lane-card-overflow)));
	height: calc(var(--moments-lane-cell-height) + (2 * var(--moments-lane-card-overflow)));
	padding: var(--moments-lane-card-overflow);
}

.moments-lane__group {
	display: inline-block;
}

.moments-lane__loading-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.45); // stylelint-disable-line
	z-index: 5; // stylelint-disable-line
}

.moments-lane__loading-spinner {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.35);
	border-top-color: var(--color-soft-white);
	animation: moments-lane-spin 0.8s linear infinite;
}

@keyframes moments-lane-spin {
	to {
		transform: rotate(360deg);
	}
}

.moments-lane__group-inner {
	position: relative;
	width: var(--moments-lane-card-width);
	height: var(--moments-lane-card-height);
	border-radius: var(--t-border-radius-card--lg);
	overflow: hidden;
	outline: 2px solid transparent;
	outline-offset: 2px;
	transition: transform $trans-time-fast, border-color $trans-time-fast, filter $trans-time-fast;
	will-change: transform, border-color;
	cursor: pointer;
	transform-origin: center center;

	&::before {
		content: '';
		position: absolute;
		inset: 0 auto auto 0;
		width: var(--moments-lane-card-width);
		height: var(--moments-lane-card-top-slice-height);
		background-color: var(--color-moments-dark);
	}

	&::after {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		width: var(--moments-lane-card-width);
		height: var(--moments-lane-card-bottom-slice-height);
		background-color: var(--color-moments-light);
		transform: skew(0deg, var(--moments-lane-angle));
	}

	.moments-lane__group.-highlighted & {
		outline-color: var(--color-hype-yellow);
	}

	@media (hover:hover) {
		.moments-lane__group:hover & {
			transform: scale(1.09);
		}
	}
}

.moments-lane__club-logo {
	--logo-size: var(--moments-lane-logo-size);
	--logo-h-offset: var(--moments-lane-logo-offset);
	--logo-v-offset: var(--moments-lane-logo-offset);
	position: absolute;
	object-fit: contain;
	z-index: 2;

	&.-top {
		width: var(--logo-size);
		height: var(--logo-size);
		left: var(--logo-h-offset);
		top: var(--logo-v-offset);
	}

	&.-bottom {
		width: var(--logo-size);
		height: var(--logo-size);
		right: var(--logo-h-offset);
		bottom: var(--logo-v-offset);
	}
}

.moments-lane__tags {
	position: relative;
	z-index: 2;
	width: 100%;
	height: 100%;
}

.moments-lane__tag {
	position: absolute;
	display: flex;
	align-items: center;
	font-size: var(--moments-lane-tag-size); // stylelint-disable-line
	letter-spacing: -1px; // stylelint-disable-line
	line-height: 1;
	white-space: nowrap;
	transform-origin: center;

	&.-top {
		left: 0;
		bottom: calc(50% - 0.02em); // optical correction for line height
		font-weight: 800; // stylelint-disable-line
		color: var(--color-moments-light);
		transform: rotate(var(--moments-lane-angle));
	}

	&.-bottom {
		right: 0;
		top: calc(50%);
		font-weight: 400; // stylelint-disable-line
		color: var(--color-moments-dark);
		transform: rotate(calc(var(--moments-lane-angle)));
	}
}
</style>
